import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  LOCALE_ID,
  OnInit,
  Output,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup } from '@angular/forms';
import { forkJoin, of } from 'rxjs';
import { catchError, map, switchMap, takeWhile } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { OrdersBaseComponent } from '@components/admin/managed-board/base/orders-base/orders-base.component';
import { OrderForm } from '@classes/order-form';
import { Order, LogElement } from '@models/order';
import { Patient } from '@models/patient';
import { Store } from '@models/store';
import { ProductElement } from '@models/product';
import { Vendor } from '@models/vendor';
import { AuthService } from '@services/auth.service';
import { NotificationService } from '@services/notification.service';
import { OrderService } from '@services/order.service';
import { PatientService } from '@services/patient.service';
import { ProductService } from '@services/product.service';
import { StoreService } from '@services/store.service';
import { VendorOrderService } from '@services/vendor-order.service';
import { ReturnOrderDialogComponent, ReturnOrderDialogResult } from '@components/admin/patient/return-order-dialog/return-order-dialog.component';
import { RestyleOrderDialogComponent } from '@components/admin/patient/restyle-order-dialog/restyle-order-dialog.component';
import { InternalNoteDialogComponent } from '@components/admin/patient/internal-note-dialog/internal-note-dialog.component';

@Component({
  selector: 'app-patient-order-details',
  templateUrl: './patient-order-details.component.html',
  styleUrls: ['./patient-order-details.component.css'],
})
export class PatientOrderDetailsComponent
  extends OrdersBaseComponent
  implements OnInit
{
  /** When set, load this order instead of reading route params (dialog/embed). */
  @Input() embedPatientId: string;
  @Input() embedOrderId: string;
  @Input() embedded = false;
  @Output() closeEmbed = new EventEmitter<{ saved?: boolean }>();

  patient: Patient;
  orderFormGroup: FormGroup;
  loaded = false;
  isNewOrder = false;
  patientId: string;
  currentStore: Store;
  panelOpenStateFrameDetails = true;
  panelOpenStatePrescription = false;
  panelOpenStateLens = false;
  panelOpenStateRepair = false;
  private orderChangedInEmbed = false;

  get hasInternalNote(): boolean {
    return !!String(this.orderFormGroup?.get('notes')?.value || '').trim();
  }

  constructor(
    @Inject(LOCALE_ID) public locale: string,
    public auth: AuthService,
    public orderService: OrderService,
    public vendorOrderService: VendorOrderService,
    public productService: ProductService,
    public storeService: StoreService,
    public cdRef: ChangeDetectorRef,
    public fb: FormBuilder,
    public route: ActivatedRoute,
    public notificationService: NotificationService,
    private patientService: PatientService,
    private router: Router,
    private dialog: MatDialog
  ) {
    super(
      locale,
      auth,
      orderService,
      vendorOrderService,
      productService,
      storeService,
      cdRef,
      fb,
      route,
      notificationService
    );
  }

  ngOnInit(): void {
    this.getAuthorized();
    this.currentStore = this.auth.getStoreObject();
    this.auth.getStoreObjectMessage().subscribe((store: Store) => {
      this.currentStore = store;
    });

    if (this.embedded && this.embedPatientId && this.embedOrderId) {
      this.loadOrder(this.embedPatientId, this.embedOrderId);
      return;
    }

    this.route.paramMap
      .pipe(
        switchMap((params) => {
          this.patientId = params.get('id');
          const orderId = params.get('orderId');
          this.isNewOrder = orderId === 'new';

          return forkJoin({
            patient: this.patientService.getPatient(this.patientId),
            order: this.isNewOrder
              ? this.createNewOrderWithReservedInvoice()
              : this.orderService.getOrderById(orderId),
          });
        })
      )
      .subscribe(({ patient, order }) => {
        this.applyLoadedOrder(patient, order);
      });
  }

  private loadOrder(patientId: string, orderId: string): void {
    this.patientId = patientId;
    this.isNewOrder = orderId === 'new';
    forkJoin({
      patient: this.patientService.getPatient(patientId),
      order: this.isNewOrder
        ? this.createNewOrderWithReservedInvoice()
        : this.orderService.getOrderById(orderId),
    }).subscribe(({ patient, order }) => {
      this.applyLoadedOrder(patient, order);
    });
  }

  private createNewOrderWithReservedInvoice() {
    return this.orderService.reserveInvoiceNumber().pipe(
      map((invoiceNumber) => {
        const order = this.createNewOrder();
        order.invoiceNumber = invoiceNumber;
        return order;
      }),
      catchError((err) => {
        console.error('reserveInvoiceNumber failed', err);
        this.notificationService.show(
          'error',
          'Could not reserve invoice number. You can still save after fixing connectivity.',
          'OK'
        );
        return of(this.createNewOrder());
      })
    );
  }

  private applyLoadedOrder(patient: Patient, order: Order): void {
    this.patient = patient;
    this.orderFormGroup = OrderForm.initOrderFormGroup(
      order,
      this.auth,
      this.storeOptions
    );
    this.orderFormGroup.get('products.0.duty').enable();
    this.ensureOrderDateDefaultsToToday();
    this.autofillInitials();
    this.loaded = true;
    this.cdRef.detectChanges();
  }

  createNewOrder(): Order {
    const newProduct = new ProductElement();
    newProduct.quantity = 1;
    newProduct.frame.frameSource = 'PATIENT';
    newProduct.product.vendor = new Vendor();
    newProduct.product.vendor.images = null;
    newProduct.product.store = this.auth.getStoreObject();

    const newOrder = new Order();
    newOrder.patient = this.patientId;
    newOrder.status = '';
    newOrder.type = 'O';
    // mat-datepicker needs a Date object (not a formatted string) to display
    newOrder.date = new Date() as any;
    newOrder.user = this.auth.currentUser._id;
    newOrder.store = this.auth.getStore();
    newOrder.products.push(newProduct);

    return newOrder;
  }

  private ensureOrderDateDefaultsToToday(): void {
    const dateControl = this.orderFormGroup?.get('date');
    if (!dateControl) {
      return;
    }

    const value = dateControl.value;
    if (value instanceof Date && !isNaN(value.getTime())) {
      return;
    }

    if (value) {
      const parsed = new Date(value);
      if (!isNaN(parsed.getTime())) {
        dateControl.setValue(parsed, { emitEvent: false });
        return;
      }
    }

    if (this.isNewOrder || !value) {
      dateControl.setValue(new Date(), { emitEvent: false });
    }
  }

  openInternalNoteDialog(): void {
    const notesControl = this.orderFormGroup?.get('notes');
    if (!notesControl) {
      return;
    }

    const dialogRef = this.dialog.open(InternalNoteDialogComponent, {
      width: '520px',
      data: { notes: notesControl.value || '' },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === undefined) {
        return;
      }
      notesControl.setValue(String(result || ''));
      notesControl.markAsDirty();
      this.cdRef.detectChanges();
    });
  }

  onSave(destination: string): void {
    this.ensureInitialsFilled();

    if (destination === 'RETURN') {
      this.returnOrder();
      return;
    }

    if (destination === 'RE-STYLE') {
      this.restyleOrder();
      return;
    }

    if (destination === 'VOID') {
      this.voidOrder();
      return;
    }

    if (destination === 'ADD TO CART') {
      this.addToCart();
      return;
    }

    const newOrder = this.orderFormGroup.get('products.0._id').value == '';
    this.applyDestinationMetadata(destination);

    if (destination == 'ADDED TO INVENTORY') {
      const invoiceNumber = this.orderFormGroup.get('invoiceNumber').value;
      this.orderFormGroup.get('invoiceNumber').setValue(invoiceNumber + '-C');
      this.orderFormGroup.get('status').setValue('CANCELED');

      this.productService
        .incrementProductInventoryQuantity(
          this.orderFormGroup.get('products.0.product').value,
          1
        )
        .subscribe((incrementProduct) => {
          if (!this.orderFormGroup.get('products.0.product._id').value) {
            this.orderFormGroup
              .get('products.0.product._id')
              .setValue(incrementProduct.product._id);

            this.vendorOrderService
              .addProductIdToVendorOrder(this.orderFormGroup.value)
              .subscribe(() => {});
          }
          this.persistOrder(newOrder, destination);
        });
      return;
    }

    this.persistOrder(newOrder, destination);
  }

  private applyDestinationMetadata(destination: string): void {
    const frameSourceControl = this.orderFormGroup.get('products.0.frame.frameSource');
    const todoControl = this.orderFormGroup.get('todo');
    const statusControl = this.orderFormGroup.get('status');
    const orderType = this.orderFormGroup.get('type')?.value;

    switch (destination) {
      case 'ORDERED FRAME':
      case 'ORDERED':
        todoControl.setValue('ORDERED');
        frameSourceControl.setValue('PATIENT');
        statusControl.setValue(this.getAwaitingStatusForType(orderType));
        break;
      case 'ORDERED ON APPROVAL':
        todoControl.setValue('ORDERED ON APPROVAL');
        frameSourceControl.setValue('PATIENT APPROVAL');
        statusControl.setValue(this.getAwaitingStatusForType(orderType));
        break;
      case 'SEND TO STACK':
        todoControl.setValue('ORDER');
        frameSourceControl.setValue(
          frameSourceControl.value === 'PATIENT APPROVAL'
            ? 'PATIENT APPROVAL'
            : 'PATIENT'
        );
        // Stack To-Do only — must NOT be AWAITING * or it appears in Awaiting Frames
        statusControl.setValue(this.getStackOrderStatusForType(orderType));
        statusControl.markAsDirty();
        break;
      case 'SAVE/HOLD':
        todoControl.setValue('HOLD');
        frameSourceControl.setValue(
          frameSourceControl.value === 'PATIENT APPROVAL'
            ? 'PATIENT APPROVAL'
            : 'PATIENT'
        );
        statusControl.setValue(this.getStackOrderStatusForType(orderType));
        break;
      case 'SAVE/QUOTE':
        todoControl.setValue('QUOTE');
        statusControl.setValue('QUOTE');
        break;
      case 'SAVE':
        todoControl.setValue('SAVE');
        break;
      default:
        todoControl.setValue(destination);
        if (destination !== 'SAVE') {
          statusControl.setValue(
            this.getOrderStatusString(orderType, destination)
          );
        }
        break;
    }
  }

  private addToCart(): void {
    const newOrder = this.orderFormGroup.get('products.0._id').value == '';
    const product = this.orderFormGroup.get('products.0.product')?.value;
    const quantity =
      parseInt(this.orderFormGroup.get('products.0.quantity')?.value, 10) || 1;
    const isPof = !!this.orderFormGroup.get('products.0.frame.pof')?.value;

    this.orderFormGroup.get('todo').setValue('ADD TO CART');
    this.orderFormGroup.get('status').setValue('DISPENSED');
    this.orderFormGroup.get('products.0.frame.frameSource').setValue(
      isPof ? 'PATIENT' : this.orderFormGroup.get('products.0.frame.frameSource')?.value || 'PATIENT'
    );

    const persist = () => this.persistOrder(newOrder, 'ADD TO CART');

    if (!isPof && product?._id) {
      this.productService
        .incrementProductInventoryQuantity(product, -Math.abs(quantity))
        .subscribe({
          next: () => persist(),
          error: () => {
            this.notificationService.show(
              'error',
              'COULD NOT DEDUCT INVENTORY',
              'OK'
            );
          },
        });
      return;
    }

    persist();
  }

  private voidOrder(): void {
    const order: Order = this.orderFormGroup.value;
    const newOrder = this.orderFormGroup.get('products.0._id').value == '';
    const clearTaxAndCancel = () => {
      this.orderFormGroup.get('products.0.tax').setValue(0);
      this.orderFormGroup.get('tax')?.setValue(0);
      this.orderFormGroup.get('todo').setValue('VOID');
      this.orderFormGroup.get('status').setValue('CANCELED');
    };

    if (!this.hasPaymentOccurred(order)) {
      clearTaxAndCancel();
      this.persistOrder(newOrder, 'VOID');
      return;
    }

    const dialogRef = this.dialog.open(ReturnOrderDialogComponent, {
      width: '250px',
      data: { message: 'VOID ORDER? ENTER STORE CREDIT', order, creditOnly: true },
    });

    dialogRef
      .afterClosed()
      .pipe(takeWhile((result) => !!result))
      .subscribe((result: ReturnOrderDialogResult) => {
        let credit =
          result?.netCredit ??
          result?.credit ??
          dialogRef.componentInstance.buildResult().netCredit;
        if (typeof credit === 'string') {
          credit = parseFloat(String(credit).replace(/,/g, ''));
        }
        if (isNaN(credit as number)) {
          credit = 0;
        }

        clearTaxAndCancel();

        const logItem = new LogElement();
        logItem.initials =
          order['initials'] || this.auth.currentUser.initials;
        logItem.user = this.auth.currentUser._id;
        logItem.item = 'VOIDED';
        logItem.notes = credit ? `STORE CREDIT: ${credit}` : '';
        logItem.date = Date.now().toString();
        const logControl = this.orderFormGroup.get('log');
        const currentLog = Array.isArray(logControl?.value) ? logControl.value : [];
        logControl?.setValue([logItem, ...currentLog]);

        this.saveFrame(this.orderFormGroup, newOrder).subscribe(() => {
          const saveMessage = this.getSaveSuccessMessage('VOID');
          if (credit && this.patientId) {
            this.patientService.getPatient(this.patientId).subscribe((patient) => {
              patient.storeCredit =
                (parseFloat(String(patient.storeCredit || 0)) || 0) + credit;
              this.patientService.updatePatient(patient).subscribe(() => {
                this.navigateBack({ saveMessage });
              });
            });
          } else {
            this.navigateBack({ saveMessage });
          }
        });
      });
  }

  private hasPaymentOccurred(order: Order): boolean {
    if (order?.status === 'DISPENSED') {
      return true;
    }
    const tax = parseFloat(
      String(order?.products?.[0]?.tax ?? order?.tax ?? 0).replace(/,/g, '')
    );
    if (!isNaN(tax) && tax > 0 && order?.status && order.status !== 'QUOTE') {
      return true;
    }
    const log = Array.isArray(order?.log) ? order.log : [];
    return log.some((entry) =>
      /PAID|PAYMENT|ADD TO CART|DISPENSED/i.test(String(entry?.item || ''))
    );
  }

  persistOrder(newOrder: boolean, destination: string): void {
    const announceNewOrder = this.isNewOrder || newOrder;

    // Re-apply right before save so Frame Status mat-select cannot keep AWAITING *
    if (destination === 'SEND TO STACK' || destination === 'SAVE/HOLD') {
      const status = this.getStackOrderStatusForType(
        this.orderFormGroup.get('type')?.value
      );
      this.orderFormGroup.get('status').setValue(status, { emitEvent: false });
      this.orderFormGroup
        .get('todo')
        .setValue(destination === 'SAVE/HOLD' ? 'HOLD' : 'ORDER', {
          emitEvent: false,
        });
    }

    this.saveFrameWithDestination(this.orderFormGroup, newOrder, destination).subscribe(
      () => {
        this.isNewOrder = false;
        if (this.embedded) {
          this.orderChangedInEmbed = true;
        }
        const saveMessage = this.getSaveSuccessMessage(destination);
        if (destination === 'SAVE') {
          this.notificationService.show('success', saveMessage, 'OK');
          this.autofillInitials();
          this.cdRef.detectChanges();
          return;
        }
        this.navigateBack({
          orderAdded: announceNewOrder,
          saveMessage,
        });
      }
    );
  }

  getSaveSuccessMessage(destination: string): string {
    switch (destination) {
      case 'SEND TO STACK':
        return 'Order successfully sent to stack';
      case 'ORDERED FRAME':
      case 'ORDERED':
        return 'Order successfully sent to awaiting frames';
      case 'ORDERED ON APPROVAL':
        return 'Order successfully ordered on approval';
      case 'SAVE/HOLD':
        return 'Order successfully saved to hold';
      case 'SAVE/QUOTE':
        return 'Order successfully saved as quote';
      case 'ADD TO CART':
        return 'Order successfully added to cart';
      case 'VOID':
        return 'Order successfully voided';
      case 'SAVE':
        return 'Order successfully saved';
      case 'RETURN':
        return 'Order successfully returned; store credit issued';
      case 'RE-STYLE':
        return 'Order re-styled; new job created';
      case 'ADDED TO INVENTORY':
        return 'Order successfully added to inventory';
      default:
        return 'Order successfully saved';
    }
  }

  /**
   * Saves the order and forces stack/awaiting status onto the POST body so the
   * Frame Status dropdown cannot override destination metadata.
   */
  private saveFrameWithDestination(
    order: FormGroup,
    newOrder: boolean,
    destination: string
  ) {
    this.fieldOpen = true;

    if (!order.controls.notes.dirty) {
      order.controls.notes.setValue('');
    }
    if (order.value._id == 'NEW') {
      order.controls._id.setValue(null);
    }
    for (let x = 0; x < order.value.products.length; x++) {
      if (order.value.products[x]._id == 'NEW') {
        order.get(`products.${x}._id`).setValue(null);
      }
      let cost = order.get(`products.${x}.cost`);
      let retail = order.get(`products.${x}.retail`);
      if (typeof cost.value === 'string') {
        cost.setValue(parseFloat(cost.value.replace(/,/g, '')));
      }
      if (typeof retail.value === 'string') {
        retail.setValue(parseFloat(retail.value.replace(/,/g, '')));
      }
    }
    this.productCosts[order.value._id] = order.get('products.0.cost').value;

    this.ensureInitialsFilled();

    if (!newOrder) {
      this.addDirtyValuesToLog(order);
    } else {
      this.addCurrentActionToLog(order, order.value.todo);
    }

    const payload = order.getRawValue();
    if (destination === 'SEND TO STACK' || destination === 'SAVE/HOLD') {
      payload.status = this.getStackOrderStatusForType(payload.type);
      payload.todo = destination === 'SAVE/HOLD' ? 'HOLD' : 'ORDER';
    } else if (
      destination === 'ORDERED FRAME' ||
      destination === 'ORDERED' ||
      destination === 'ORDERED ON APPROVAL'
    ) {
      payload.status = this.getAwaitingStatusForType(payload.type);
    }

    return this.orderService.updateOrderFull(payload).pipe(
      map((order2) => {
        this.finalizeOrderSave(order, order2);
        return true;
      })
    );
  }

  getOrderStatusString(type, destination): string {
    return /^ORDERED/.test(destination)
      ? this.getAwaitingStatusForType(type)
      : this.getStackOrderStatusForType(type);
  }

  /** Status for Stack To-Do / Hold — excluded from Awaiting Frames. */
  getStackOrderStatusForType(type: string): string {
    switch (type) {
      case 'A':
        return 'ORDER ACCESSORY';
      case 'F':
        return 'ORDER PART';
      case 'O':
      case 'N':
      default:
        return 'ORDER FRAME';
    }
  }

  /** Status set when frame is ordered from the Stack — appears in Awaiting Frames. */
  getAwaitingStatusForType(type: string): string {
    switch (type) {
      case 'A':
        return 'AWAITING ACCESSORY';
      case 'F':
        return 'AWAITING PART';
      case 'O':
      case 'N':
      default:
        return 'AWAITING FRAME';
    }
  }

  navigateBack(options?: {
    orderAdded?: boolean;
    saveMessage?: string;
  }): void {
    if (this.embedded) {
      if (options?.saveMessage) {
        this.notificationService.show('success', options.saveMessage, 'OK');
      }
      this.closeEmbed.emit({
        saved: this.orderChangedInEmbed || !!options?.orderAdded,
      });
      return;
    }
    this.router.navigate(['/admin/patient', this.patientId], {
      state: {
        ...(options?.orderAdded ? { orderAdded: true } : {}),
        ...(options?.saveMessage
          ? { orderSaveMessage: options.saveMessage }
          : {}),
      },
    });
  }

  getLoggedInInitials(): string {
    const fromUser = String(this.auth?.currentUser?.initials || '').trim();
    if (fromUser) {
      return fromUser.toUpperCase();
    }
    return String(this.auth?.initials || '').trim().toUpperCase();
  }

  autofillInitials(): void {
    const control = this.orderFormGroup?.get('initials');
    if (!control) {
      return;
    }
    const initials = this.getLoggedInInitials();
    if (initials) {
      control.setValue(initials);
    }
  }

  ensureInitialsFilled(): void {
    const control = this.orderFormGroup?.get('initials');
    if (!control) {
      return;
    }
    if (!String(control.value || '').trim()) {
      this.autofillInitials();
    }
  }

  returnOrder(): void {
    const order: Order = this.orderFormGroup.value;
    const dialogRef = this.dialog.open(ReturnOrderDialogComponent, {
      width: '320px',
      data: { message: 'RETURN ORDER?', order },
    });

    dialogRef
      .afterClosed()
      .pipe(takeWhile((result) => !!result))
      .subscribe((result: ReturnOrderDialogResult) => {
        const dialogResult = result?.netCredit !== undefined
          ? result
          : dialogRef.componentInstance.buildResult();
        const credit = dialogResult.netCredit;
        const returnToInventory = dialogResult.returnToInventory !== false;

        const logItem = new LogElement();
        logItem.initials = order['initials'];
        logItem.user = this.auth.currentUser._id;
        logItem.item = 'RETURNED';
        logItem.notes =
          dialogResult.restockingFee > 0
            ? `CREDIT ${dialogResult.credit}; FEE ${dialogResult.restockingFee}; NET ${credit}`
            : '';
        logItem.date = Date.now().toString();
        order.log.unshift(logItem);
        order.invoiceNumber = order.invoiceNumber + '-R';
        order.patient = this.patientId;

        this.orderService
          .returnOrder(order, credit, returnToInventory)
          .subscribe(() => {
            this.orderFormGroup.get('status').setValue('RETURNED');
            this.navigateBack({
              saveMessage: this.getSaveSuccessMessage('RETURN'),
            });
          });
      });
  }

  restyleOrder(): void {
    const originalInvoice = String(
      this.orderFormGroup.get('invoiceNumber')?.value || ''
    ).trim();
    const dialogRef = this.dialog.open(RestyleOrderDialogComponent, {
      width: '420px',
      data: {
        invoiceNumber: originalInvoice,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeWhile((result) => !!result))
      .subscribe(() => {
        this.ensureInitialsFilled();

        const logItem = new LogElement();
        logItem.initials =
          String(this.orderFormGroup.get('initials')?.value || '').trim() ||
          this.getLoggedInInitials();
        logItem.user = this.auth.currentUser._id;
        logItem.item = 'RE-STYLED';
        logItem.notes = '';
        logItem.date = Date.now().toString();
        this.prependLogEntry(this.orderFormGroup, logItem);

        this.orderFormGroup.get('status').setValue('RE-STYLED');
        this.orderFormGroup.get('todo').setValue('RE-STYLED');

        const originalPayload = this.orderFormGroup.getRawValue();
        this.normalizeOrderMoneyFields(originalPayload);
        const newOrder = this.buildRestyleOrder(originalPayload, originalInvoice);

        this.orderService
          .updateOrderFull(originalPayload)
          .pipe(
            switchMap(() => this.orderService.reserveInvoiceNumber()),
            switchMap((invoiceNumber) => {
              newOrder.invoiceNumber = invoiceNumber;
              return this.orderService.addOrder(newOrder);
            })
          )
          .subscribe((createdOrders: Order[]) => {
            const created = Array.isArray(createdOrders)
              ? createdOrders[0]
              : createdOrders;
            const newOrderId = created?._id;
            const saveMessage = this.getSaveSuccessMessage('RE-STYLE');

            if (this.embedded) {
              this.orderChangedInEmbed = true;
              this.notificationService.show('success', saveMessage, 'OK');
              this.closeEmbed.emit({ saved: true });
              return;
            }

            this.notificationService.show('success', saveMessage, 'OK');

            if (newOrderId) {
              this.router.navigate([
                '/admin/patient',
                this.patientId,
                'order',
                newOrderId,
              ]);
              return;
            }

            this.navigateBack({
              orderAdded: true,
              saveMessage,
            });
          });
      });
  }

  private buildRestyleOrder(original: any, originalInvoice: string): Order {
    const clone = JSON.parse(JSON.stringify(original || {}));
    clone._id = '';
    clone.invoiceNumber = '';
    clone.status = this.getStackOrderStatusForType(clone.type || 'O');
    clone.todo = 'ORDER';
    clone.date = new Date();
    clone.dateAdded = undefined;
    clone.dateUpdated = undefined;
    clone.notes = '';
    clone.vendorOrder = null;
    clone.patient = this.patientId;
    clone.user = this.auth.currentUser._id;
    clone.store = this.auth.getStore();
    delete clone.initials;

    (clone.products || []).forEach((productElement: any) => {
      productElement._id = null;
    });

    const logItem = new LogElement();
    logItem.initials =
      String(original?.initials || '').trim() || this.getLoggedInInitials();
    logItem.user = this.auth.currentUser._id;
    logItem.item = originalInvoice
      ? `RE-STYLE FROM ${originalInvoice}`
      : 'RE-STYLE';
    logItem.notes = '';
    logItem.date = Date.now().toString();
    clone.log = [logItem];

    this.normalizeOrderMoneyFields(clone);
    return clone as Order;
  }

  private normalizeOrderMoneyFields(order: any): void {
    const products = order?.products;
    if (!Array.isArray(products)) {
      return;
    }
    products.forEach((productElement: any) => {
      if (typeof productElement?.cost === 'string') {
        productElement.cost = parseFloat(productElement.cost.replace(/,/g, '')) || 0;
      }
      if (typeof productElement?.retail === 'string') {
        productElement.retail =
          parseFloat(productElement.retail.replace(/,/g, '')) || 0;
      }
      if (typeof productElement?.tax === 'string') {
        productElement.tax = parseFloat(productElement.tax.replace(/,/g, '')) || 0;
      }
    });
  }
}
